/**
 * Utility function to trap keyboard focus within a modal dialog.
 *
 * This function ensures that when the modal is open, keyboard navigation using the TAB key
 * cycles only through the focusable elements inside the modal, preventing focus from moving
 * outside the modal window.
 *
 * @param {HTMLElement} element - The modal container element to trap focus within.
 *
 * Usage example:
 *   const modal = document.getElementById('myModal');
 *   trapFocusOnModal(modal);
 *
 * Accessibility:
 *   Improves accessibility by keeping keyboard users within the modal until it is closed.
 */
export const trapFocusOnModal = element => {
  const focusableEls = Array.from(
    element.querySelectorAll(
      '[tabindex="0"], a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="email"]:not([disabled]), input[type="password"]:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
    )
  ).filter(el => !el.closest('[tabindex="-1"]'));
  if (focusableEls.length) {
    element.setAttribute("tabindex", "-1");
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];
    const KEYCODE_TAB = 9;

    firstFocusableEl.focus();

    const keyboardNavigation = (e) => {
      const isTabPressed = e.key === "Tab" || e.keyCode === KEYCODE_TAB;
      if (!isTabPressed) {
        return;
      }
      if (e.shiftKey) {
        /* shift + tab */
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } else {
        /* tab */
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    };

    element.addEventListener("keydown", keyboardNavigation);

    // Restituisci la funzione per poterla rimuovere successivamente
    return keyboardNavigation;
  }
};

export const removeTrapFocusOnModal = (element, keyboardNavigationHandler) => {
  if(element) element.removeAttribute("tabindex", "-1");
  if (keyboardNavigationHandler && element) {
    element.removeEventListener("keydown", keyboardNavigationHandler);
  }
};
